'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack, HStack, Divider } from '@terra/ui-styled-system';
import { LabelDefault, MetaDefault } from '../../../components/typography';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Tooltip } from '../../../components/tooltip';
import { Scroll } from '../../Scroll';
import { VirtualizedStack } from '../../virtualized-stack';
import {
  ArrowShrinkIcon,
  ArrowLeft01Icon,
  ExpandIcon,
  Globe02Icon,
  RefreshIcon,
  Search01Icon,
} from '@terra/ui-icons';
import { CustomTreeNode } from './CustomTreeNode';
import { splitDomainTreeRootsForListFilter } from '../utils/domain-tree-archived-roots';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import {
  getVisibleNodes,
  type DomainTree,
  type DomainTreeNode,
  type VisibleNode,
} from '../utils/domain-tree.utils';

type DomainTreeListItem =
  | { kind: 'node'; node: DomainTreeNode; level: number }
  | { kind: 'separator' };

const toNodeListItems = (visibleNodes: VisibleNode[]): DomainTreeListItem[] =>
  visibleNodes.map(({ node, level }) => ({ kind: 'node', node, level }));

export type DomainTreeContentProps = {
  domainTree: DomainTree;
  selectedDomains: string[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleExpandAll: () => void;
  handleCollapseAll: () => void;
  handleToggleSelection: (nodeId: string, checked: boolean) => void;
  handleToggleExpanded: (nodeId: string) => void;
  nodeIdToCountMap?: Record<string, number>;
  isNodeSelectable?: (node: DomainTreeNode) => boolean;
  onToggleCollapse?: () => void;
  hasActiveFilter?: boolean;
  selectionMode?: 'multiple' | 'single';
  disableCollapsible?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  listLifecycleState?: LifecycleStateFilterDto;
  showHeader?: boolean;
  showSearch?: boolean;
  hideZeroCountNodes?: boolean;
  cascadeSelectionState?: boolean;
  allNodesSelected?: boolean;
};
export function DomainTreeContent({
  domainTree,
  selectedDomains,
  searchTerm,
  setSearchTerm,
  handleExpandAll,
  handleCollapseAll,
  handleToggleSelection,
  handleToggleExpanded,
  nodeIdToCountMap,
  isNodeSelectable,
  onToggleCollapse,
  hasActiveFilter = false,
  selectionMode = 'multiple',
  disableCollapsible,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  listLifecycleState,
  showHeader = true,
  showSearch = true,
  hideZeroCountNodes = false,
  cascadeSelectionState = false,
  allNodesSelected = false,
}: DomainTreeContentProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const tCommon = useTranslations('designSystem.common.entityReferenceFilter');
  const selectedDomainsSet = useMemo<ReadonlySet<string>>(
    () => new Set(selectedDomains),
    [selectedDomains],
  );
  const isAllExpanded = useMemo(() => {
    for (const node of domainTree.nodeMap.values()) {
      if (node.children.length > 0 && !node.isExpanded) {
        return false;
      }
    }

    return true;
  }, [domainTree]);
  const handleToggleExpandCollapse = () => {
    if (isAllExpanded) {
      handleCollapseAll();
    } else {
      handleExpandAll();
    }
  };

  const visibleItems = useMemo(() => {
    const { leadingRoots, archivedRoots } = splitDomainTreeRootsForListFilter({
      roots: domainTree.roots,
      listLifecycleState,
    });
    const leadingItems = toNodeListItems(
      getVisibleNodes(leadingRoots, searchTerm).filter(
        ({ node }) =>
          !hideZeroCountNodes || (nodeIdToCountMap?.[node.id] ?? 0) > 0,
      ),
    );
    const archivedItems = toNodeListItems(
      getVisibleNodes(archivedRoots, searchTerm).filter(
        ({ node }) =>
          !hideZeroCountNodes || (nodeIdToCountMap?.[node.id] ?? 0) > 0,
      ),
    );

    if (leadingItems.length === 0 || archivedItems.length === 0) {
      return [...leadingItems, ...archivedItems];
    }

    return [...leadingItems, { kind: 'separator' as const }, ...archivedItems];
  }, [
    domainTree.roots,
    hideZeroCountNodes,
    listLifecycleState,
    nodeIdToCountMap,
    searchTerm,
  ]);

  const getVisibleItemKey = ({
    item,
    index,
  }: {
    item: DomainTreeListItem;
    index: number;
  }) =>
    item.kind === 'separator' ? `archived-separator-${index}` : item.node.id;

  const renderVisibleItem = ({ item }: { item: DomainTreeListItem }) => {
    if (item.kind === 'separator') {
      return (
        <Box py={2} px={1}>
          <Divider />
        </Box>
      );
    }

    return (
      <CustomTreeNode
        node={item.node}
        level={item.level}
        selectedDomains={selectedDomainsSet}
        onToggleSelection={handleToggleSelection}
        onToggleExpanded={handleToggleExpanded}
        nodeIdToCountMap={nodeIdToCountMap}
        isNodeSelectable={isNodeSelectable}
        cascadeSelectionState={cascadeSelectionState}
        allNodesSelected={allNodesSelected}
        selectionMode={selectionMode}
      />
    );
  };

  return (
    <VStack
      w="full"
      h="full"
      minH="0"
      gap={2}
      px={4}
      pb={3}
      pt={showHeader ? 4 : 2}
      alignItems="stretch"
    >
      {showHeader ? (
        <Box position="relative">
          <HStack justifyContent="space-between" alignItems="center">
            <LabelDefault color="fg.default">
              {t('filters.domainHierarchy')}
            </LabelDefault>
            <HStack gap={0} alignItems="center">
              {onRefresh && (
                <Tooltip title={t('filters.refresh')}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    color="fg.muted"
                    onClick={onRefresh}
                    aria-label={t('filters.refresh')}
                    disabled={isRefreshing}
                  >
                    <RefreshIcon
                      size={16}
                      className={isRefreshing ? 'animate-spin' : undefined}
                    />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={
                  isAllExpanded ? t('filters.minimize') : t('filters.expand')
                }
              >
                <IconButton
                  variant="ghost"
                  size="xs"
                  color="fg.muted"
                  onClick={handleToggleExpandCollapse}
                  aria-label={
                    isAllExpanded
                      ? t('filters.collapseAll')
                      : t('filters.expandAll')
                  }
                >
                  {isAllExpanded ? (
                    <ArrowShrinkIcon size={18} />
                  ) : (
                    <ExpandIcon size={18} />
                  )}
                </IconButton>
              </Tooltip>
              {!disableCollapsible && onToggleCollapse && (
                <IconButton
                  variant="ghost"
                  size="xs"
                  color="fg.muted"
                  onClick={onToggleCollapse}
                  aria-label={t('filters.collapseDomainTree')}
                >
                  <ArrowLeft01Icon size={18} />
                </IconButton>
              )}
            </HStack>
          </HStack>
          {hasActiveFilter && (
            <Box
              position="absolute"
              top={0}
              right={0}
              w="6px"
              h="6px"
              bg="colorPalette.emphasized"
              borderRadius="full"
              borderWidth="1px"
              borderColor="bg.canvas"
            />
          )}
        </Box>
      ) : null}

      {showSearch ? (
        <Box position="relative">
          <Box
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            color="fg.muted"
          >
            <Search01Icon size={14} />
          </Box>
          <Input
            placeholder={t('filters.searchDomains')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            pl="32px"
            size="sm"
            bg="bg.default"
          />
        </Box>
      ) : null}

      {showHeader || showSearch ? <Divider /> : null}

      {isLoading ? (
        <Scroll orientation="vertical" showScrollbar={false} flex={1} minH="0">
          <VStack gap={3} alignItems="center" justifyContent="center" py={8}>
            <MetaDefault color="fg.muted" textAlign="center">
              {tCommon('loading')}
            </MetaDefault>
          </VStack>
        </Scroll>
      ) : domainTree.roots.length === 0 ? (
        <Scroll orientation="vertical" showScrollbar={false} flex={1} minH="0">
          <VStack gap={3} alignItems="center" justifyContent="center" py={8}>
            <Globe02Icon size={32} color="fg.muted" />
            <MetaDefault color="fg.muted" textAlign="center">
              {t('filters.noDomains')}
            </MetaDefault>
          </VStack>
        </Scroll>
      ) : (
        <Box flex={1} minH="0" overflow="hidden">
          <VirtualizedStack
            data={visibleItems}
            computeItemKey={getVisibleItemKey}
            renderItem={renderVisibleItem}
            increaseViewportBy={300}
            overscan={200}
          />
        </Box>
      )}
    </VStack>
  );
}
